import { useMemo } from 'react';

import {
  ageFromBucket,
  estimateWorkoutCalories,
  resolveAthleteEnergy,
  sessionEnergyReadout,
  type MovementPattern,
  type Workout,
  type WorkoutEnergyReadout,
} from '@heraion/shared';

import type { LoggerProfile } from './hostDeps';
import { useHcWinsCalories } from './state/hcCaloriesPreference';

type PatternsBySlug = Readonly<Record<string, MovementPattern | null | undefined>>;
type EnergyProfile = Pick<LoggerProfile, 'bodyweightKg' | 'sex' | 'ageBucket'>;

/** A saved session's energy readout, read from its STORED fields (HC-8 B2c):
 *  the measured value and effort, not a live re-estimate. Shared by the
 *  logger's past-session edit and the summary a past session opens on (#318). */
export function storedEnergyReadout(
  w: Workout,
  patternsBySlug: PatternsBySlug,
  profile: EnergyProfile,
  hcWins: boolean,
): WorkoutEnergyReadout {
  const readout = sessionEnergyReadout(w, {
    hcWins,
    ageYears: ageFromBucket(profile.ageBucket),
  });
  const touchesHc =
    readout.resolved?.source === 'hc' ||
    readout.resolved?.estimate?.method === 'keytel';
  if (!touchesHc) return readout;
  // LEGAL-5 ripiego (2026-09-09): the canonical figure touches Health
  // Connect, so recompute the SAME estimator with HR stripped. `w` is the
  // assembled Workout (session + LogSet rows), so the raw per-set
  // ingredients ARE here, unlike the thin Session envelope web's
  // session-detail view reads (which gets no ripiego).
  const metOnly = estimateWorkoutCalories(
    w,
    patternsBySlug,
    resolveAthleteEnergy(profile),
    { includeHr: false },
  );
  return { ...readout, metFallbackKcal: metOnly?.kcal ?? null };
}

/** The same readout for a surface outside the logger, with the device's
 *  «HC wins» preference. Null until the workout is loaded. */
export function useStoredEnergyReadout(
  w: Workout | null,
  patternsBySlug: PatternsBySlug,
  profile: EnergyProfile,
): WorkoutEnergyReadout | null {
  const hcWins = useHcWinsCalories().enabled;
  return useMemo(
    () => (w ? storedEnergyReadout(w, patternsBySlug, profile, hcWins) : null),
    [w, patternsBySlug, profile, hcWins],
  );
}
